/**
 * Splitting a zone's display string back into its location and zone halves.
 *
 * A zone is named `"{location}-{zone}"`, and that whole string is what any surface showing a
 * zone renders — the location half is what identifies it, since names like "Main Floor"
 * repeat across hundreds of clients. But a card that also shows the location on its own line
 * then says the client twice, and surfaces that want each half to link somewhere different
 * need to know where the seam is.
 *
 * THE SEAM CANNOT BE FOUND BY LOOKING FOR A HYPHEN. Both halves contain them freely. A
 * location called "Gleichner-Rippin" holding a zone called "Vandervort, Hauck and Greenholt"
 * gives "Gleichner-Rippin-Vandervort, Hauck and Greenholt" — split at the first hyphen and
 * the location becomes "Gleichner". Split at the last and a zone called "Bernier-Jerde"
 * loses half its own name. Both of those are real rows in the seed data.
 *
 * The only reliable split is against the location's OWN display string, which every surface
 * doing this already has to hand.
 */

/** A zone display string taken apart, or left whole when it cannot be split safely. */
export interface ZoneDisplayStringParts {
  /**
   * The location half, or null when the zone name does not open with it — a record indexed
   * before the location prefix was restored, a record fetched from an endpoint that does not
   * eager-load it, or a zone genuinely not named after its location.
   */
  location: string | null;
  /** The zone half when the split succeeded, otherwise the whole display string. */
  zone: string;
}

/** Read a trimmed, non-empty display string off a `{ display_string }` ref, else null. */
export function readZoneDisplayString(value: unknown): string | null {
  if (value && typeof value === 'object' && 'display_string' in value) {
    const display = (value as { display_string?: unknown }).display_string;
    if (typeof display === 'string' && display.trim() !== '') {
      return display;
    }
  }

  return null;
}

/**
 * The location to split a zone's name against: the one carried INSIDE the zone
 * reference when the document has it, the caller's sibling column otherwise.
 *
 * The nested reference is the one telling the truth. A sibling column can name
 * a different location entirely — a master zone's name is prefixed with the
 * MASTER's location while the row it sits on holds the subscriber's — and most
 * rows carrying a zone have no location column at all. The sibling stays as a
 * fallback for documents written before the nested location existed.
 *
 * Lives here rather than in ZoneDisplayString because a second surface needs
 * the same answer: the container zone line asks whether the heading it just
 * drew already names the location, and it has to ask it exactly the same way.
 */
export function resolveZoneLocation(zone: unknown, siblingLocation: unknown): unknown {
  if (zone && typeof zone === 'object' && 'location' in zone) {
    const nested = (zone as { location?: unknown }).location;
    if (readZoneDisplayString(nested) !== null) {
      return nested;
    }
  }

  return siblingLocation;
}

/**
 * Split `zoneDisplayString` into its location and zone halves using the location's own name.
 *
 * Returns `location: null` and the untouched string as `zone` whenever the split is not
 * certain — no location to match, a name that merely CONTAINS the location rather than
 * opening with it ("Old Hill Inc Annex" under "Hill Inc"), or a split that would leave an
 * empty zone half. Callers then render the whole string as one thing, which is always
 * correct if less useful.
 */
export function splitZoneDisplayString(
  zoneDisplayString: string | null | undefined,
  locationDisplayString: string | null | undefined,
): ZoneDisplayStringParts {
  const zone = typeof zoneDisplayString === 'string' ? zoneDisplayString : '';
  const location = typeof locationDisplayString === 'string' ? locationDisplayString : '';

  if (zone === '' || location === '') {
    return { location: null, zone };
  }

  const prefix = `${location}-`;
  if (!zone.startsWith(prefix)) {
    return { location: null, zone };
  }

  const remainder = zone.slice(prefix.length);
  if (remainder === '') {
    // "Hill Inc-" with nothing after it: splitting would render a link with no label.
    return { location: null, zone };
  }

  return { location, zone: remainder };
}
